import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import { SelectContainer, MenuButton, ListBox, FocusedOption, Label } from "@xo-union/ui-select";
import Icon from "@xo-union/tk-component-icons";
import classesPropType from "@xo-union/classes-prop-type";
import { useClasses } from "@xo-union/react-css-modules";
import { useUniqueID } from "@xo-union/react-unique-id";
import cx from "classnames";
import upperFirst from "lodash/upperFirst";
import lowerCase from "lodash/lowerCase";
import styles from "./styles.css";
import WithValidations from "../WithValidations.js";
import SubText from "../SubText.js";
const Select = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    // Container props
    defaultValue,
    value,
    onChange,
    onOpen,
    isOpen,
    onClose,
    disabled,
    // Other props
    name,
    label = upperFirst(lowerCase(name)),
    state,
    subText,
    children,
    classes: classesProp,
    id: idProp,
    onBlur,
    ...props
  } = _ref;
  const classes = useClasses(styles, classesProp);
  const id = useUniqueID(idProp);
  let subTextId;
  if (subText) {
    subTextId = `${id}-subtext`;
  }
  return /*#__PURE__*/React.createElement(SelectContainer, {
    disabled: disabled,
    value: value,
    defaultValue: defaultValue,
    onChange: onChange,
    onOpen: onOpen,
    onClose: onClose,
    isOpen: isOpen
  }, select => {
    const iconName = select.isOpen ? 'caret_up' : 'caret_down';
    const dataHasValue = select.hasFocusedOption ? '' : undefined;
    const containerClasses = cx(classes.container, {
      [classes['is-open']]: select.isOpen,
      [classes['is-closed']]: !select.isOpen
    });
    const inputClasses = cx(classes.input, {
      [classes['is-neutral']]: state === 'neutral',
      [classes['is-invalid']]: state === 'invalid',
      [classes['is-valid']]: state === 'valid',
      [classes.disabled]: select.disabled
    });
    return /*#__PURE__*/React.createElement("div", {
      onBlur: onBlur,
      className: containerClasses
    }, /*#__PURE__*/React.createElement(MenuButton, _extends({
      "aria-describedby": subTextId,
      className: inputClasses,
      "data-has-value": dataHasValue,
      type: "button"
    }, props, {
      ref: ref
    }), /*#__PURE__*/React.createElement(FocusedOption, null)), /*#__PURE__*/React.createElement(ListBox, {
      className: classes.list
    }, children), /*#__PURE__*/React.createElement(Label, {
      as: "div",
      className: classes.label
    }, label), /*#__PURE__*/React.createElement(Icon, {
      name: iconName,
      size: "md",
      className: classes.caret
    }), /*#__PURE__*/React.createElement("input", {
      type: "hidden",
      name: name,
      value: select.value
    }), subText && /*#__PURE__*/React.createElement(SubText, {
      state: state,
      className: classes['sub-text'],
      id: subTextId
    }, subText));
  });
});
const validationProp = process.env.NODE_ENV !== "production" ? PropTypes.any : {};
process.env.NODE_ENV !== "production" ? Select.propTypes = {
  /**
   * Input name. Used to assume label
   *
   * When validations are enabled, the name is used in the messaging by some validators.
   */
  name: PropTypes.string.isRequired,
  /**
   * Set field label
   */
  label: PropTypes.string,
  /**
   * Visual state. This has no effect when validations are enabled.
   */
  state: PropTypes.oneOf(['neutral', 'valid', 'invalid']),
  /**
   * Subtext message used to provide the user with extra information. The subText will appear just below the field.
   *
   *
   * When validations are enabled, this value is used for the `neutral` and the `valid` state (if `subTextOnValid` is not specified). The given validators will determine what subText will render on `invalid`, unless otherwise specified by the `subTextOnInvalid` prop.
   */
  subText: PropTypes.node,
  /**
   * Contents of list box. Should possibly be Option elements
   */
  children: PropTypes.node,
  /**
   * Override ID
   */
  id: PropTypes.string,
  /**
   * Classes API
   */
  classes: classesPropType(['container', 'is-open', 'is-closed', 'input', 'is-neutral', 'is-valid', 'is-invalid', 'disabled', 'sub-text', 'list', 'label', 'caret']),
  /**
   * Disable select
   */
  disabled: PropTypes.bool,
  /**
   * Opt into controlled mode and set the value. This value has to match a value of a child option
   */
  value: PropTypes.string,
  /**
   * Called when a new option is selected
   */
  onChange: PropTypes.func,
  /**
   * Set the initial value when Select is uncontrolled
   */
  defaultValue: PropTypes.string,
  /**
   * Called when menu is opened
   */
  onOpen: PropTypes.func,
  /**
   * Called when menu is closed
   */
  onClose: PropTypes.func,
  /**
   * Opt into controlled mode for the open/closed state
   */
  isOpen: PropTypes.bool,
  /**
   * Validations to apply. See [Input Component](/packages/@xo-union/ui-form-validations#input).
   */
  validations: validationProp,
  /**
   * This is only used when validations are enabled.
   *
   * What subText to render when state is `invalid`. When a function is provided, the function will receive the errors generated by the validators. The return value should be a React.Node type.
   */
  subTextOnInvalid: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * This is only used when validations are enabled.
   *
   * What subText to render when state is `valid`. This is only used when validations are enabled.
   */
  subTextOnValid: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Called on blur
   */
  onBlur: PropTypes.func
} : void 0;
Select.defaultProps = {
  state: 'neutral'
};
export default WithValidations(Select);